import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { safePrint } from "@/utils/safePrint";
import { formatDateDDMonYY } from "@/utils/dateFormat";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isWithinInterval, parseISO } from "date-fns";
import { 
  Package,
  Truck,
  Loader2,
  CheckCircle,
  ClipboardList,
  CalendarIcon,
  FileText,
  Edit,
  Save,
  Printer,
  Send,
  Undo2,
  Trash2,
  Search,
  Settings,
  Check,
  X,
  Activity,
  Clock,
  RotateCcw,
  FileSpreadsheet
} from "lucide-react";

export default function Warehouse() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeTab, setActiveTab] = useState("to_sort");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTrip, setSelectedTrip] = useState("T1");
  const [searchQuery, setSearchQuery] = useState(""); // Search state
  
  // Printer settings state
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);
  const [printerName, setPrinterName] = useState(() => {
    return localStorage.getItem('warehouse_printer_name') || '';
  });
  
  // Trip details state
  const [tripDetails, setTripDetails] = useState({});
  const [showTripDetailsModal, setShowTripDetailsModal] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  
  // Trip grade overrides for print purposes only (not saved to DB)
  
  // Order Tracking
  const [showTracking, setShowTracking] = useState(false);
  const [trackingData, setTrackingData] = useState([]);
  const [trackingView, setTrackingView] = useState('day');
  const [trackingDate, setTrackingDate] = useState(new Date());
  const [trackingLoading, setTrackingLoading] = useState(false);
  
  // Returns tab
  const [returnsData, setReturnsData] = useState([]);
  const [returnsLoading, setReturnsLoading] = useState(false);
  const [confirmingReturnId, setConfirmingReturnId] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [postingReturnId, setPostingReturnId] = useState(null);
  const [gradeOverrides, setGradeOverrides] = useState({});
  const [editingGradeOrder, setEditingGradeOrder] = useState(null);
  const [gradeEditText, setGradeEditText] = useState("");
  
  const [tripForm, setTripForm] = useState({
    driver_name: "",
    driver_phone: "",
    vehicle_number: "",
    dispatch_time: "",
    notes: ""
  });
  
  // Dispatched orders editable state
  const [dispatchedEdits, setDispatchedEdits] = useState({});
  
  // Customers for transport name lookup
  const [customers, setCustomers] = useState([]);
  
  // Transport name edits
  const [transportEdits, setTransportEdits] = useState({});

  const tripOptions = ["T1", "T2", "T3", "T4", "T5"];
  
  // Resolve transport name: order field > customer record > fallback empty
  const getTransportName = (order) => {
    if (order?.transporter_name) return order.transporter_name;
    if (order?.transport_name) return order.transport_name;
    if (order?.customer_id) {
      const customer = customers.find(c => c.id === order.customer_id);
      if (customer?.transporter_name) return customer.transporter_name;
    }
    return '';
  };

  // Save printer name to localStorage
  const savePrinterSettings = () => {
    localStorage.setItem('warehouse_printer_name', printerName);
    toast.success(`Printer settings saved: ${printerName || 'Default'}`);
    setShowPrinterSettings(false);
  };

  const fetchOrders = useCallback(async () => {
    try {
      const [ordersRes, customersRes] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/customers`)
      ]);
      setOrders(ordersRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTripDetails = useCallback(async () => {
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const response = await axios.get(`${API}/trips?date=${dateStr}`);
      const detailsMap = {};
      response.data.forEach(trip => {
        detailsMap[trip.trip_no] = trip;
      });
      setTripDetails(detailsMap);
    } catch (error) {
    }
  }, [selectedDate]);

  const fetchReturns = useCallback(async () => {
    setReturnsLoading(true);
    try {
      const res = await axios.get(`${API}/returns`);
      setReturnsData(res.data || []);
    } catch (e) { }
    finally { setReturnsLoading(false); }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchReturns();
  }, [fetchOrders, fetchReturns]);

  useEffect(() => {
    if (activeTab === 'trip_summary') {
      fetchTripDetails();
    }
  }, [activeTab, selectedDate, fetchTripDetails]);

  const fetchTracking = useCallback(async () => {
    setTrackingLoading(true);
    try {
      const res = await axios.get(`${API}/orders/tracking`);
      setTrackingData(res.data || []);
    } catch (e) { }
    finally { setTrackingLoading(false); }
  }, []);

  useEffect(() => { if (showTracking) fetchTracking(); }, [showTracking, fetchTracking]);
  useEffect(() => { if (activeTab === 'returns') fetchReturns(); }, [activeTab, fetchReturns]);

  const confirmReturn = async (returnId) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setConfirmingReturnId(returnId);
    try {
      const res = await axios.post(`${API}/returns/${returnId}/confirm`, {
        confirmed_by: user.id || '',
        confirmed_by_username: user.username || 'admin',
      });
      toast.success(res.data?.message || "Return confirmed");
      fetchReturns();
      if (selectedReturn?.id === returnId) {
        setSelectedReturn(prev => ({ ...prev, status: 'confirmed', confirmed_by_username: user.username || 'admin' }));
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to confirm return"); }
    finally { setConfirmingReturnId(null); }
  };

  const postReturnToSheet = async (returnId) => {
    setPostingReturnId(returnId);
    try {
      const res = await axios.post(`${API}/returns/${returnId}/post-to-sheet`);
      toast.success(res.data?.message || "Return posted to Google Sheets");
      fetchReturns();
      if (selectedReturn?.id === returnId) {
        setSelectedReturn(prev => ({ ...prev, posted_to_sheet: true }));
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to post to sheet"); }
    finally { setPostingReturnId(null); }
  };

  const getTrackingFiltered = () => {
    const now = trackingDate;
    if (trackingView === 'day') {
      const ds = format(now, 'yyyy-MM-dd');
      return trackingData.filter(o => {
        const dates = [o.sent_to_sort_at, o.preparing_at, o.ready_at, o.sequenced_at, o.dispatched_at].filter(Boolean);
        return dates.some(d => d.substring(0, 10) === ds);
      });
    }
    let start, end;
    if (trackingView === 'week') {
      start = startOfWeek(now, { weekStartsOn: 1 });
      end = endOfWeek(now, { weekStartsOn: 1 });
    } else {
      start = startOfMonth(now);
      end = endOfMonth(now);
    }
    return trackingData.filter(o => {
      const dates = [o.sent_to_sort_at, o.preparing_at, o.ready_at, o.sequenced_at, o.dispatched_at].filter(Boolean);
      return dates.some(d => {
        try { return isWithinInterval(parseISO(d), { start, end }); } catch { return false; }
      });
    });
  };

  const fmtTs = (ts) => {
    if (!ts) return '-';
    try {
      return new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
    } catch { return '-'; }
  };

  const fmtDuration = (from, to) => {
    if (!from || !to) return '-';
    try {
      const diff = new Date(to) - new Date(from);
      const hrs = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      if (hrs > 24) return `${Math.floor(hrs / 24)}d ${hrs % 24}h`;
      return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
    } catch { return '-'; }
  };


  // Filter orders
  const toSortOrders = orders.filter(order => order.status === 'to_sort');
  const readyToDispatchOrders = orders
    .filter(order => order.status === 'ready_to_dispatch')
    .sort((a, b) => {
      const tripA = tripOptions.indexOf(a.trip_no) >= 0 ? tripOptions.indexOf(a.trip_no) : 999;
      const tripB = tripOptions.indexOf(b.trip_no) >= 0 ? tripOptions.indexOf(b.trip_no) : 999;
      if (tripA !== tripB) return tripA - tripB;
      return (a.loading_seq || 999) - (b.loading_seq || 999);
    });

  // Calculate total qty for each section
  const toSortTotalQty = toSortOrders.reduce((sum, order) => {
    if (order.items?.length > 0) {
      return sum + order.items.reduce((s, i) => s + (i.qty || 0), 0);
    }
    return sum + (order.total_qty || order.quantity_kg || 0);
  }, 0);

  const readyTotalQty = readyToDispatchOrders.reduce((sum, order) => {
    if (order.items?.length > 0) {
      return sum + order.items.reduce((s, i) => s + (i.qty || 0), 0);
    }
    return sum + (order.total_qty || order.quantity_kg || 0);
  }, 0);

  // Filter by selected date for trip summary
  const filteredReadyOrders = readyToDispatchOrders.filter(order => {
    const orderDate = new Date(order.updated_at || order.created_at);
    return orderDate.toDateString() === selectedDate.toDateString();
  });

  // Get dispatched orders for the date
  // Use dispatched_at for date filtering (NOT updated_at) to prevent orders 
  // from disappearing when editing LR/Transport/Invoice details
  const dispatchedOrders = orders.filter(order => {
    if (order.status !== 'dispatched') return false;
    // Prefer dispatched_at, fall back to updated_at, then created_at
    const dispatchDateStr = order.dispatched_at || order.updated_at || order.created_at;
    if (!dispatchDateStr) return false;
    // Extract just the date part (YYYY-MM-DD) from the stored date string
    const dispatchDateOnly = dispatchDateStr.substring(0, 10); // "2026-03-08"
    // Format selected date as YYYY-MM-DD in local timezone
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const selectedDateOnly = `${year}-${month}-${day}`;
    return dispatchDateOnly === selectedDateOnly;
  });

  // Get dispatched trips for the day (to disable in dropdown)
  const dispatchedTripsToday = [...new Set(dispatchedOrders.map(o => o.trip_no).filter(Boolean))];

  // Group dispatched orders by trip for summary display
  const dispatchedTripGroups = dispatchedOrders.reduce((groups, order) => {
    const tripNo = order.trip_no || 'Unassigned';
    if (!groups[tripNo]) groups[tripNo] = [];
    groups[tripNo].push(order);
    return groups;
  }, {});

  // Search filter function
  const filterBySearch = (orderList) => {
    if (!searchQuery.trim()) return orderList;
    const query = searchQuery.toLowerCase().trim();
    return orderList.filter(order => 
      order.order_number?.toLowerCase().includes(query) ||
      order.customer_name?.toLowerCase().includes(query)
    );
  };

  // Check if order matches search for highlighting
  const isSearchMatch = (order) => {
    if (!searchQuery.trim()) return false;
    const query = searchQuery.toLowerCase().trim();
    return order.order_number?.toLowerCase().includes(query);
  };

  // Group orders by trip
  const tripGroups = filteredReadyOrders.reduce((groups, order) => {
    const tripNo = order.trip_no || 'Unassigned';
    if (!groups[tripNo]) groups[tripNo] = [];
    groups[tripNo].push(order);
    return groups;
  }, {});

  Object.keys(tripGroups).forEach(tripNo => {
    tripGroups[tripNo].sort((a, b) => (a.loading_seq || 999) - (b.loading_seq || 999));
  });

  // Get trip stats
  const getTripStats = (tripOrders) => {
    let totalQty = 0;
    const skuTotals = {};
    tripOrders.forEach(order => {
      if (order.items?.length > 0) {
        order.items.forEach(item => {
          const sku = item.sku || 'Other';
          const qty = item.qty || 0;
          totalQty += qty;
          skuTotals[sku] = (skuTotals[sku] || 0) + qty;
        });
      } else {
        const sku = order.packing || 'Other';
        const qty = order.total_qty || order.quantity_kg || 0;
        totalQty += qty;
        skuTotals[sku] = (skuTotals[sku] || 0) + qty;
      }
    });
    const skuBreakdown = Object.entries(skuTotals).map(([sku, qty]) => `${qty} ${sku}`).join(' / ');
    return { totalQty, skuBreakdown };
  };

  const markAsSorted = async (orderId) => {
    const user = getCurrentUser();
    try {
      await axios.put(`${API}/orders/${orderId}`, { 
        status: 'ready_to_dispatch',
        sorted_by: user?.id,
        sorted_by_username: user?.username
      });
      toast.success("Order marked as Ready to Dispatch");
      fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error("Failed to update order");
    }
  };

  // Get grade display text for an order (with override for print)
  const getGradeDisplay = (order) => {
    if (gradeOverrides[order.id]) return gradeOverrides[order.id];
    return order.items?.length > 0 
      ? order.items.map(i => `${i.grade}(${i.qty})`).join(', ')
      : `${order.product_grade || '-'}(${order.total_qty || order.quantity_kg || 0})`;
  };

  // Start editing grade for an order
  const startEditGrade = (order) => {
    setEditingGradeOrder(order.id);
    setGradeEditText(gradeOverrides[order.id] || getGradeDisplay(order));
  };

  // Save grade override (display only, not saved to DB)
  const saveGradeOverride = (orderId) => {
    setGradeOverrides(prev => ({ ...prev, [orderId]: gradeEditText }));
    setEditingGradeOrder(null);
    setGradeEditText("");
  };

  // Send order back to previous status
  const sendBackOrder = async (orderId, targetStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { status: targetStatus });
      toast.success(`Order sent back to ${targetStatus === 'pending' ? 'Orders' : 'To Sort'}`);
      fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error("Failed to send back order");
    }
  };

  // Print detailed token for order with all details
  // Different content based on order status
  const printToken = (order, fromSection = null) => {
    const qty = order.items?.length > 0 
      ? order.items.reduce((sum, i) => sum + (i.qty || 0), 0) 
      : (order.total_qty || order.quantity_kg || 0);
    const dispatchCode = order.dispatch_code || '-';
    const timestamp = new Date().toLocaleString('en-IN', { 
      day: '2-digit', month: 'short', year: 'numeric', 
      hour: '2-digit', minute: '2-digit', hour12: true 
    });
    
    // Determine section context
    const section = fromSection || order.status;
    const isToSort = section === 'to_sort';
    const isReadyToDispatch = section === 'ready_to_dispatch';
    
    // Group items by SKU for To Sort section
    const tinItems = (order.items || []).filter(i => (i.sku || 'TIN').toUpperCase() === 'TIN');
    const bucketItems = (order.items || []).filter(i => (i.sku || '').toUpperCase() === 'BUCKET');
    const otherItems = (order.items || []).filter(i => !['TIN', 'BUCKET'].includes((i.sku || 'TIN').toUpperCase()));
    
    // Build grades HTML based on section
    let gradesHtml = '';
    
    // If grade override exists AND printing from trip_summary, use it for print
    if (gradeOverrides[order.id] && section === 'trip_summary') {
      const overrideText = gradeOverrides[order.id];
      // Parse "W320(2), W400(3)" or "W320 2, W400 3" into grade/qty rows
      const entries = overrideText.split(',').map(s => s.trim()).filter(Boolean);
      const rows = entries.map((entry, idx) => {
        const match = entry.match(/^([A-Za-z0-9\s]+?)\s*[\(\s]+(\d+)\s*\)?$/) || entry.match(/^(\S+)\s+(\d+)$/);
        if (match) {
          return { grade: match[1].trim(), qty: match[2] };
        }
        return { grade: entry, qty: '' };
      });
      gradesHtml = `
        <table style="width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 11px;">
          <thead>
            <tr style="background: #000; color: white;">
              <th style="padding: 5px; width: 25px; text-align: left;">#</th>
              <th style="padding: 5px; text-align: left;">Grade</th>
              <th style="padding: 5px; text-align: right;">Qty</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map((r, idx) => `
              <tr style="border-bottom: 1px solid #000;">
                <td style="padding: 4px; text-align: center;">${idx + 1}</td>
                <td style="padding: 4px; font-weight: bold;">${r.grade}</td>
                <td style="padding: 4px; text-align: right; font-weight: bold;">${r.qty}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>`;
    } else if (isToSort && (tinItems.length > 0 || bucketItems.length > 0)) {
      // To Sort: Show TIN and BUCKET separately
      if (tinItems.length > 0) {
        const tinQty = tinItems.reduce((sum, i) => sum + (i.qty || 0), 0);
        gradesHtml += `
          <div style="margin-bottom: 10px;">
            <div style="background: #000; color: white; padding: 4px 8px; font-weight: bold; font-size: 11px; border: 1px solid #000;">TIN (${tinQty})</div>
            <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
              ${tinItems.map((item, idx) => `
                <tr style="border-bottom: 1px solid #000;">
                  <td style="padding: 4px; width: 25px; text-align: center;">${idx + 1}</td>
                  <td style="padding: 4px; font-weight: bold;">${item.grade}</td>
                  <td style="padding: 4px; text-align: right; font-weight: bold;">${item.qty}</td>
                </tr>
              `).join('')}
            </table>
          </div>
        `;
      }
      if (bucketItems.length > 0) {
        const bucketQty = bucketItems.reduce((sum, i) => sum + (i.qty || 0), 0);
        gradesHtml += `
          <div style="margin-bottom: 10px;">
            <div style="background: #000; color: white; padding: 4px 8px; font-weight: bold; font-size: 11px; border: 1px solid #000;">BUCKET (${bucketQty})</div>
            <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
              ${bucketItems.map((item, idx) => `
                <tr style="border-bottom: 1px solid #000;">
                  <td style="padding: 4px; width: 25px; text-align: center;">${idx + 1}</td>
                  <td style="padding: 4px; font-weight: bold;">${item.grade}</td>
                  <td style="padding: 4px; text-align: right; font-weight: bold;">${item.qty}</td>
                </tr>
              `).join('')}
            </table>
          </div>
        `;
      }
      if (otherItems.length > 0) {
        const otherQty = otherItems.reduce((sum, i) => sum + (i.qty || 0), 0);
        gradesHtml += `
          <div style="margin-bottom: 10px;">
            <div style="background: #000; color: white; padding: 4px 8px; font-weight: bold; font-size: 11px; border: 1px solid #000;">OTHER (${otherQty})</div>
            <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
              ${otherItems.map((item, idx) => `
                <tr style="border-bottom: 1px solid #000;">
                  <td style="padding: 4px; width: 25px; text-align: center;">${idx + 1}</td>
                  <td style="padding: 4px; font-weight: bold;">${item.grade}</td>
                  <td style="padding: 4px;">${item.sku || '-'}</td>
                  <td style="padding: 4px; text-align: right; font-weight: bold;">${item.qty}</td>
                </tr>
              `).join('')}
            </table>
          </div>
        `;
      }
    } else {
      // Standard table for other sections
      gradesHtml = order.items?.length > 0 
        ? `<table style="width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 11px;">
            <thead>
              <tr style="background: #000; color: white;">
                <th style="padding: 5px; width: 25px; text-align: left;">#</th>
                <th style="padding: 5px; text-align: left;">Grade</th>
                <th style="padding: 5px; text-align: left;">Pack</th>
                <th style="padding: 5px; text-align: right;">Qty</th>
              </tr>
            </thead>
            <tbody>
              ${order.items.map((item, idx) => `
                <tr style="border-bottom: 1px solid #000;">
                  <td style="padding: 4px; text-align: center;">${idx + 1}</td>
                  <td style="padding: 4px; font-weight: bold;">${item.grade}</td>
                  <td style="padding: 4px;">${item.sku || 'TIN'}</td>
                  <td style="padding: 4px; text-align: right; font-weight: bold;">${item.qty}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>`
        : `<table style="width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 11px;">
            <tbody>
              <tr style="border-bottom: 1px solid #000;">
                <td style="padding: 4px; text-align: center;">1</td>
                <td style="padding: 4px; font-weight: bold;">${order.product_grade || '-'}</td>
                <td style="padding: 4px;">${order.packing || 'TIN'}</td>
                <td style="padding: 4px; text-align: right; font-weight: bold;">${qty}</td>
              </tr>
            </tbody>
          </table>`;
    }
    
    // Build info rows based on section
    // To Sort: NO customer name, NO bill type
    // Ready to Dispatch: NO bill type, but has customer name
    let infoRowsHtml = '';
    
    if (!isToSort) {
      // Show customer name for ready_to_dispatch and other sections
      infoRowsHtml += `<div style="display: flex; justify-content: space-between; margin: 6px 0; font-size: 11px;"><span style="font-weight: bold;">Customer:</span><span>${order.dispatch_name || order.customer_name}</span></div>`;
    }
    
    // Bill type only for dispatched (not to_sort, not ready_to_dispatch, not trip_summary)
    const isTripSummary = section === 'trip_summary';
    if (!isToSort && !isReadyToDispatch && !isTripSummary) {
      infoRowsHtml += `<div style="display: flex; justify-content: space-between; margin: 6px 0; font-size: 11px;"><span style="font-weight: bold;">Bill Type:</span><span>${order.est_type || 'EST'}</span></div>`;
    }
    
    if (dispatchCode !== '-') {
      infoRowsHtml += `<div style="display: flex; justify-content: space-between; margin: 6px 0; font-size: 11px;"><span style="font-weight: bold;">Dispatch Code:</span><span>${dispatchCode}</span></div>`;
    }
    if (order.trip_no) {
      infoRowsHtml += `<div style="display: flex; justify-content: space-between; margin: 6px 0; font-size: 11px;"><span style="font-weight: bold;">Trip:</span><span>${order.trip_no}</span></div>`;
    }
    if (order.loading_seq) {
      infoRowsHtml += `<div style="display: flex; justify-content: space-between; margin: 6px 0; font-size: 11px;"><span style="font-weight: bold;">Loading Seq:</span><span>${order.loading_seq}</span></div>`;
    }
    
    // Remark - highlighted prominently (only for to_sort and ready_to_dispatch, NOT trip_summary)
    const orderRemark = order.remark || order.notes || '';
    let remarkHtml = '';
    if (orderRemark && !isTripSummary) {
      remarkHtml = `
        <div style="border: 2px solid #000; padding: 6px 8px; margin: 8px 0;">
          <div style="font-weight: bold; font-size: 10px; margin-bottom: 2px;">REMARK:</div>
          <div style="font-size: 12px; font-weight: bold;">${orderRemark}</div>
        </div>
      `;
    }
    
    // Create print content with clearer fonts and no blur
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Token - ${order.order_number}</title>
        <style>
          @media print {
            body { margin: 0; }
          }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            padding: 15px; 
            font-size: 12px;
          }
          .token { 
            border: 2px solid #000; 
            padding: 15px; 
            width: 300px;
            background: white;
          }
          .order-number { 
            font-size: 20px; 
            font-weight: bold; 
            text-align: center; 
            padding: 10px; 
            margin-bottom: 12px; 
            border: 2px solid #000;
            letter-spacing: 1px;
          }
          .total-qty { 
            font-size: 22px; 
            font-weight: bold; 
            text-align: center; 
            margin: 12px 0; 
            padding: 10px; 
            border: 2px solid #000;
          }
          .timestamp { 
            text-align: center; 
            font-size: 10px; 
            color: #000; 
            margin-top: 12px; 
            border-top: 1px dashed #000; 
            padding-top: 8px; 
          }
        </style>
      </head>
      <body>
        <div class="token">
          <div class="order-number">${order.order_number}</div>
          ${remarkHtml}
          ${infoRowsHtml}
          ${gradesHtml}
          <div class="total-qty">TOTAL: ${qty}</div>
          <div class="timestamp">Printed: ${timestamp}</div>
        </div>
      </body>
      </html>
    `;
    
    safePrint(printContent, `Token_${order.order_number}`);
  };

  // Print Trip Summary
  const printTripSummary = (tripNo) => {
    const tripOrders = tripGroups[tripNo] || [];
    const stats = getTripStats(tripOrders);
    const details = tripDetails[tripNo] || {};
    const timestamp = new Date().toLocaleString('en-IN', { 
      day: '2-digit', month: 'short', year: 'numeric', 
      hour: '2-digit', minute: '2-digit', hour12: true 
    });
    
    const ordersHtml = tripOrders.map((order, idx) => {
      const qty = order.items?.length > 0 
        ? order.items.reduce((sum, i) => sum + (i.qty || 0), 0) 
        : (order.total_qty || order.quantity_kg || 0);
      const gradesStr = gradeOverrides[order.id] || (order.items?.length > 0 
        ? order.items.map(i => `${i.grade} ${i.qty}`).join(', ')
        : `${order.product_grade || '-'} ${qty}`);
      const transport = getTransportName(order) || '-';
      return `
        <tr>
          <td style="border: 1px solid #000; padding: 3px 5px; text-align: center;">${order.loading_seq || idx + 1}</td>
          <td style="border: 1px solid #000; padding: 3px 5px; font-weight: bold;">${order.order_number}</td>
          <td style="border: 1px solid #000; padding: 3px 5px;">${order.dispatch_name || order.customer_name}</td>
          <td style="border: 1px solid #000; padding: 3px 5px; font-size: 9px;">${transport}</td>
          <td style="border: 1px solid #000; padding: 3px 5px; font-size: 9px;">${gradesStr}</td>
          <td style="border: 1px solid #000; padding: 3px 5px; text-align: right; font-weight: bold;">${qty}</td>
        </tr>
      `;
    }).join('');
    
    const printContent = `
      <html>
      <head>
        <title>Trip Summary - ${tripNo}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 10px; font-size: 11px; max-width: 550px; margin: 0 auto; }
          .header { text-align: center; font-weight: bold; font-size: 14px; border-bottom: 2px solid #000; padding-bottom: 6px; margin-bottom: 8px; }
          .trip-info { padding: 6px 8px; margin-bottom: 8px; border: 1px solid #000; font-size: 10px; }
          .trip-info-row { display: flex; justify-content: space-between; margin: 2px 0; }
          .label { font-weight: bold; }
          .orders-table { width: 100%; border-collapse: collapse; margin: 8px 0; }
          .orders-table th { background: #000; color: white; padding: 4px 5px; text-align: left; font-size: 10px; }
          .summary { padding: 8px; margin-top: 8px; border: 2px solid #000; text-align: center; }
          .summary-qty { font-size: 16px; font-weight: bold; }
          .timestamp { text-align: center; font-size: 9px; margin-top: 8px; border-top: 1px dashed #000; padding-top: 6px; }
          @media print { body { max-width: 550px; margin: 0 auto; } }
        </style>
      </head>
      <body>
        <div class="header">PURELIFE - TRIP SUMMARY: ${tripNo}</div>
        <div class="trip-info">
          <div class="trip-info-row"><span class="label">Date:</span><span>${format(selectedDate, "dd-MMM-yy")}</span></div>
          ${details.driver_name ? `<div class="trip-info-row"><span class="label">Driver:</span><span>${details.driver_name}</span></div>` : ''}
          ${details.driver_phone ? `<div class="trip-info-row"><span class="label">Contact:</span><span>${details.driver_phone}</span></div>` : ''}
          ${details.vehicle_number ? `<div class="trip-info-row"><span class="label">Vehicle:</span><span>${details.vehicle_number}</span></div>` : ''}
          ${details.dispatch_time ? `<div class="trip-info-row"><span class="label">Dispatch Time:</span><span>${details.dispatch_time}</span></div>` : ''}
        </div>
        
        <table class="orders-table">
          <thead>
            <tr>
              <th style="width: 30px;">SEQ</th>
              <th style="width: 55px;">ORDER</th>
              <th>CUSTOMER</th>
              <th style="width: 70px;">TRANSPORT</th>
              <th>GRADES (QTY)</th>
              <th style="width: 40px; text-align: right;">QTY</th>
            </tr>
          </thead>
          <tbody>
            ${ordersHtml}
          </tbody>
        </table>
        
        <div class="summary">
          <div class="summary-qty">TOTAL: ${stats.totalQty}</div>
          <div style="font-size: 10px; margin-top: 3px;">${stats.skuBreakdown}</div>
          <div style="font-size: 10px; margin-top: 3px;">${tripOrders.length} Orders</div>
        </div>
        
        <div class="timestamp">Printed: ${timestamp}</div>
      </body>
      </html>
    `;
    
    safePrint(printContent, `Trip_Summary_${format(selectedDate, 'dd-MMM-yy')}`);
  };

  // Get current logged-in user info for audit trail
  const getCurrentUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  };

  const dispatchTrip = async (tripNo) => {
    const tripOrders = tripGroups[tripNo] || [];
    if (tripOrders.length === 0) {
      toast.error("No orders to dispatch");
      return;
    }
    
    const user = getCurrentUser();
    const dispatchTime = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    
    try {
      // Update all orders to dispatched status
      for (const order of tripOrders) {
        await axios.put(`${API}/orders/${order.id}`, { 
          status: 'dispatched',
          user_id: user?.id,
          username: user?.username,
          dispatched_at: new Date().toISOString()
        });
      }
      
      // Save dispatch time to trip details if not already set
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const existingDetails = tripDetails[tripNo] || {};
      if (!existingDetails.dispatch_time) {
        await axios.post(`${API}/trips`, { 
          trip_date: dateStr, 
          trip_no: tripNo, 
          dispatch_time: dispatchTime,
          driver_name: existingDetails.driver_name || '',
          driver_phone: existingDetails.driver_phone || '',
          vehicle_number: existingDetails.vehicle_number || '',
          notes: existingDetails.notes || ''
        });
      }
      
      toast.success(`${tripNo} dispatched (${tripOrders.length} orders)`);
      fetchOrders();
      fetchTripDetails();
    } catch (error) {
      toast.error("Failed to dispatch trip");
    }
  };

  // Reverse dispatch - move all orders back to ready_to_dispatch
  const reverseDispatch = async (tripNo) => {
    const tripOrders = dispatchedTripGroups[tripNo] || [];
    if (tripOrders.length === 0) {
      toast.error("No dispatched orders to reverse");
      return;
    }
    
    const user = getCurrentUser();
    
    try {
      // Update all orders back to ready_to_dispatch status
      for (const order of tripOrders) {
        await axios.put(`${API}/orders/${order.id}`, { 
          status: 'ready_to_dispatch',
          user_id: user?.id,
          username: user?.username,
          dispatched_at: null  // Clear dispatch time
        });
      }
      
      toast.success(`${tripNo} reversed - ${tripOrders.length} orders moved back to Ready to Dispatch`);
      fetchOrders();
      fetchTripDetails();
    } catch (error) {
      toast.error("Failed to reverse dispatch");
    }
  };

  const updateTripNo = async (orderId, tripNo) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { trip_no: tripNo || null });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, trip_no: tripNo || null } : o));
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, trip_no: tripNo || null }));
      }
    } catch (error) {
      toast.error("Failed to update trip");
    }
  };

  const updateLoadingSeq = async (orderId, seq) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { loading_seq: seq ? parseInt(seq) : null });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, loading_seq: seq ? parseInt(seq) : null } : o));
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, loading_seq: seq ? parseInt(seq) : null }));
      }
    } catch (error) {
      toast.error("Failed to update sequence");
    }
  };

  const openTripDetailsModal = (tripNo) => {
    setEditingTrip(tripNo);
    const existing = tripDetails[tripNo] || {};
    setTripForm({
      driver_name: existing.driver_name || "",
      driver_phone: existing.driver_phone || "",
      vehicle_number: existing.vehicle_number || "",
      dispatch_time: existing.dispatch_time || "",
      notes: existing.notes || ""
    });
    setShowTripDetailsModal(true);
  };

  const saveTripDetails = async () => {
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      await axios.post(`${API}/trips`, { trip_date: dateStr, trip_no: editingTrip, ...tripForm });
      toast.success("Trip details saved");
      setShowTripDetailsModal(false);
      fetchTripDetails();
    } catch (error) {
      toast.error("Failed to save trip details");
    }
  };

  // Handle dispatched order field change
  const handleDispatchedFieldChange = (orderId, field, value) => {
    setDispatchedEdits(prev => ({
      ...prev,
      [orderId]: {
        ...(prev[orderId] || {}),
        [field]: value
      }
    }));
  };

  // Get current value for dispatched order field
  const getDispatchedFieldValue = (order, field) => {
    if (dispatchedEdits[order.id] && dispatchedEdits[order.id][field] !== undefined) {
      return dispatchedEdits[order.id][field];
    }
    return order[field] || '';
  };

  // Save dispatched order details
  const saveDispatchedDetails = async (orderId) => {
    const edits = dispatchedEdits[orderId];
    if (!edits) return;
    
    try {
      await axios.put(`${API}/orders/${orderId}`, edits);
      toast.success("Details saved");
      // Update local state
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, ...edits } : o));
      // Clear edits for this order
      setDispatchedEdits(prev => {
        const newEdits = { ...prev };
        delete newEdits[orderId];
        return newEdits;
      });
    } catch (error) {
      toast.error("Failed to save details");
    }
  };

  const displayOrders = filterBySearch(activeTab === 'to_sort' ? toSortOrders : readyToDispatchOrders);
  const currentTripOrders = tripGroups[selectedTrip] || [];
  const currentTripStats = getTripStats(currentTripOrders);
  const currentTripDetails = tripDetails[selectedTrip] || {};

  // Get available trips (exclude already dispatched trips for the day)
  const getAvailableTrips = () => {
    return tripOptions.filter(t => !dispatchedTripsToday.includes(t));
  };

  return (
    <div className="p-4" data-testid="warehouse-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>Warehouse</h1>
      </div>

      {/* Tab Buttons */}
      <div className="flex gap-2 mb-3 flex-wrap items-center">
        <Button size="sm" variant={activeTab === 'to_sort' ? 'default' : 'outline'} onClick={() => { setActiveTab('to_sort'); setSelectedOrder(null); }} className="gap-1 h-8">
          <Package className="w-3 h-3" /> To Sort ({toSortOrders.length}) - {toSortTotalQty} qty
        </Button>
        <Button size="sm" variant={activeTab === 'ready_to_dispatch' ? 'default' : 'outline'} onClick={() => { setActiveTab('ready_to_dispatch'); setSelectedOrder(null); }} className="gap-1 h-8">
          <Truck className="w-3 h-3" /> Ready ({readyToDispatchOrders.length}) - {readyTotalQty} qty
        </Button>
        <Button size="sm" variant={activeTab === 'trip_summary' ? 'default' : 'outline'} onClick={() => { setActiveTab('trip_summary'); setSelectedOrder(null); }} className="gap-1 h-8">
          <ClipboardList className="w-3 h-3" /> Trip Summary
        </Button>
        <Button size="sm" variant={activeTab === 'returns' ? 'default' : 'outline'} onClick={() => { setActiveTab('returns'); setSelectedOrder(null); }} className="gap-1 h-8" data-testid="warehouse-returns-tab">
          <RotateCcw className="w-3 h-3" /> Return ({returnsData.filter(r => r.status === 'pending').length})
        </Button>
        
        {/* Spacer */}
        <div className="flex-1"></div>
        
        {/* Order Tracking Button */}
        <Button size="sm" variant="outline" onClick={() => setShowTracking(true)} className="gap-1 h-8" data-testid="warehouse-order-tracking-btn">
          <Activity className="w-3 h-3" /> Order Tracking
        </Button>
        
        {/* Printer Settings Button */}
        <Button 
          size="sm" 
          variant="outline" 
          onClick={() => setShowPrinterSettings(true)} 
          className="h-8 gap-1"
          title={printerName ? `Printer: ${printerName}` : 'Configure Printer'}
        >
          <Printer className="w-3 h-3" />
          <Settings className="w-3 h-3" />
        </Button>
      </div>

      {/* Trip Summary View */}
      {activeTab === 'trip_summary' ? (
        <div className="space-y-3">
          {/* Date Selection + Search */}
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button size="sm" variant="outline" className="gap-1 h-8">
                  <CalendarIcon className="w-3 h-3" />
                  {format(selectedDate, "dd-MMM-yy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={selectedDate} onSelect={(date) => date && setSelectedDate(date)} initialFocus />
              </PopoverContent>
            </Popover>
            
            {/* Search Input - right side after date */}
            <div className="relative ml-auto">
              <Search className="w-3 h-3 absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <Input 
                type="text"
                placeholder="Search order #..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 w-40 pl-7 text-xs"
              />
            </div>
          </div>

          {/* Main Content - Trips List + Trip Detail Side Panel */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
            {/* Trips List - Left Side */}
            <Card className="lg:col-span-3">
              <CardContent className="p-0">
                {/* Header */}
                <div className="p-2 border-b bg-secondary/30">
                  <p className="text-sm font-medium">All Trips - {format(selectedDate, "dd-MMM-yy")}</p>
                </div>

                {/* Table Headers */}
                <div className="grid grid-cols-[50px_60px_1fr_50px_120px] gap-1 px-2 py-1 bg-secondary/50 text-[10px] font-semibold border-b">
                  <span>Trip</span>
                  <span className="text-right">Qty</span>
                  <span>SKU Breakdown</span>
                  <span className="text-center">Orders</span>
                  <span className="text-center">Driver / Time / Contact</span>
                </div>

                {/* Trips List */}
                <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
                  {tripOptions.map((tripNo) => {
                    const tripOrders = tripGroups[tripNo] || [];
                    const dispatchedTripOrders = dispatchedTripGroups[tripNo] || [];
                    const stats = getTripStats(tripOrders.length > 0 ? tripOrders : dispatchedTripOrders);
                    const isSelected = selectedTrip === tripNo;
                    const isDispatched = dispatchedTripsToday.includes(tripNo);
                    const tripDetail = tripDetails[tripNo] || {};
                    
                    return (
                      <div
                        key={tripNo}
                        onClick={() => setSelectedTrip(tripNo)}
                        className={`grid grid-cols-[50px_60px_1fr_50px_120px] gap-1 px-2 py-2 border-b cursor-pointer text-xs ${
                          isSelected ? 'bg-primary/10 border-l-4 border-l-primary' : 'hover:bg-secondary/30'
                        } ${tripOrders.length === 0 && !isDispatched ? 'opacity-50' : ''} ${isDispatched ? 'bg-green-50' : ''}`}
                      >
                        <span className={`font-bold ${isDispatched ? 'text-green-700' : 'text-primary'}`}>
                          {tripNo} {isDispatched && <CheckCircle className="w-3 h-3 inline ml-1" />}
                        </span>
                        <span className="text-right font-bold">{stats.totalQty || 0}</span>
                        <span className="truncate text-muted-foreground">{stats.skuBreakdown || '-'}</span>
                        <span className="text-center">
                          <Badge variant={isDispatched ? 'default' : tripOrders.length > 0 ? 'default' : 'outline'} className={`text-[10px] ${isDispatched ? 'bg-green-600' : ''}`}>
                            {isDispatched ? `✓ ${dispatchedTripOrders.length}` : tripOrders.length}
                          </Badge>
                        </span>
                        <span className="text-center text-[9px] text-gray-600">
                          {isDispatched || tripDetail.driver_name ? (
                            <div className="flex flex-col leading-tight">
                              <span className="font-medium text-gray-800">{tripDetail.driver_name || '-'}</span>
                              <span>{tripDetail.dispatch_time ? `@ ${tripDetail.dispatch_time}` : ''} {tripDetail.driver_phone ? `| ${tripDetail.driver_phone}` : ''}</span>
                            </div>
                          ) : '-'}
                        </span>
                      </div>
                    );
                  })}
                </div>
                
              </CardContent>
            </Card>

            {/* Trip Detail - Right Side Panel */}
            <Card className="lg:col-span-2">
              <CardContent className="p-3">
                {(currentTripOrders.length > 0 || (dispatchedTripGroups[selectedTrip]?.length > 0)) ? (
                  <div className="space-y-3">
                    {/* Trip Header */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-lg">{selectedTrip} Summary</h3>
                        <p className="text-sm text-muted-foreground">
                          {currentTripOrders.length > 0 
                            ? `${currentTripStats.totalQty} qty ready (${currentTripStats.skuBreakdown})`
                            : `${getTripStats(dispatchedTripGroups[selectedTrip] || []).totalQty} qty dispatched`
                          }
                        </p>
                      </div>
                      <div className="flex gap-1">
                        {currentTripOrders.length > 0 && (
                          <Badge className="bg-blue-100 text-blue-800">{currentTripOrders.length} ready</Badge>
                        )}
                        {dispatchedTripGroups[selectedTrip]?.length > 0 && (
                          <Badge className="bg-green-100 text-green-800">{dispatchedTripGroups[selectedTrip].length} dispatched</Badge>
                        )}
                      </div>
                    </div>

                    {/* Trip Details (Driver, Vehicle) */}
                    {(currentTripDetails.driver_name || currentTripDetails.vehicle_number) && (
                      <div className="p-2 bg-blue-50 rounded border border-blue-200 text-xs">
                        <div className="grid grid-cols-2 gap-2">
                          {currentTripDetails.driver_name && <div><span className="text-muted-foreground">Driver:</span> {currentTripDetails.driver_name}</div>}
                          {currentTripDetails.vehicle_number && <div><span className="text-muted-foreground">Vehicle:</span> {currentTripDetails.vehicle_number}</div>}
                          {currentTripDetails.dispatch_time && <div><span className="text-muted-foreground">Time:</span> {currentTripDetails.dispatch_time}</div>}
                          {currentTripDetails.driver_phone && <div><span className="text-muted-foreground">Phone:</span> {currentTripDetails.driver_phone}</div>}
                        </div>
                      </div>
                    )}

                    {/* Ready Orders in Trip */}
                    {currentTripOrders.length > 0 && (
                      <div className="border rounded overflow-hidden">
                        <div className="px-2 py-1 bg-blue-50 text-xs font-semibold text-blue-800">Ready to Dispatch</div>
                        <div className="grid grid-cols-[14px_25px_60px_0.8fr_70px_1fr_40px_24px] gap-1 px-2 py-1.5 bg-secondary/50 text-[10px] font-semibold border-b">
                          <span></span><span>SEQ</span><span>ORDER</span><span>CUSTOMER</span><span>TRANSPORT</span><span>GRADES</span><span className="text-right">QTY</span><span></span>
                        </div>
                        <div className="max-h-[250px] overflow-y-auto">
                        {currentTripOrders.map((order, idx) => {
                          const qty = order.items?.length > 0 ? order.items.reduce((sum, i) => sum + (i.qty || 0), 0) : (order.total_qty || order.quantity_kg || 0);
                          const hasRemark = !!(order.remark || order.notes);
                          const gradeText = getGradeDisplay(order);
                          const isEditingThis = editingGradeOrder === order.id;
                          return (
                            <div key={order.id} className="grid grid-cols-[14px_25px_60px_0.8fr_70px_1fr_40px_24px] gap-1 px-2 py-1.5 border-b text-xs items-center">
                              <span>{hasRemark && <span className="inline-block w-2.5 h-2.5 rounded-full bg-orange-400 cursor-pointer" title={order.remark || order.notes} onClick={(e) => { e.stopPropagation(); toast.info(order.remark || order.notes, { duration: 5000 }); }}></span>}</span>
                              <span className="font-bold text-primary">{order.loading_seq || idx + 1}</span>
                              <span className="font-mono font-semibold text-[10px]">{order.order_number}</span>
                              <span className="truncate text-[10px] text-muted-foreground">{order.dispatch_name || order.customer_name}</span>
                              <span className="truncate text-[10px] text-gray-600">{getTransportName(order) || '-'}</span>
                              <div className="min-w-0">
                                {isEditingThis ? (
                                  <div className="flex items-center gap-0.5">
                                    <Input
                                      value={gradeEditText}
                                      onChange={(e) => setGradeEditText(e.target.value)}
                                      className="h-5 text-[10px] flex-1 min-w-0"
                                      autoFocus
                                      onKeyDown={(e) => { if (e.key === 'Enter') saveGradeOverride(order.id); if (e.key === 'Escape') setEditingGradeOrder(null); }}
                                    />
                                    <Button size="sm" className="h-5 w-5 p-0 bg-green-600 shrink-0" onClick={() => saveGradeOverride(order.id)}>
                                      <Check className="w-3 h-3" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-5 w-5 p-0 shrink-0" onClick={() => setEditingGradeOrder(null)}>
                                      <X className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <span 
                                    className={`text-[10px] cursor-pointer truncate block px-1 py-0.5 rounded ${gradeOverrides[order.id] ? 'bg-blue-50 border border-blue-300 text-blue-800 font-medium' : 'hover:bg-gray-100'}`}
                                    onClick={() => startEditGrade(order)}
                                    title="Click to edit grades for print"
                                  >
                                    {gradeText}
                                  </span>
                                )}
                              </div>
                              <span className="text-right font-bold text-[10px]">{qty}</span>
                              <div className="text-center">
                                <Button size="sm" variant="outline" className="h-5 w-5 p-0" onClick={() => printToken(order, 'trip_summary')} title="Print Token">
                                  <Printer className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                        </div>
                      </div>
                    )}

                    {/* Dispatched Orders in Trip */}
                    {dispatchedTripGroups[selectedTrip]?.length > 0 && (
                      <div className="border rounded overflow-hidden">
                        <div className="px-2 py-1 bg-green-50 text-xs font-semibold text-green-800">Dispatched</div>
                        <div className="grid grid-cols-[14px_25px_60px_0.8fr_70px_1fr_40px_24px] gap-1 px-2 py-1.5 bg-secondary/50 text-[10px] font-semibold border-b">
                          <span></span><span>SEQ</span><span>ORDER</span><span>CUSTOMER</span><span>TRANSPORT</span><span>GRADES</span><span className="text-right">QTY</span><span></span>
                        </div>
                        <div className="max-h-[150px] overflow-y-auto">
                          {dispatchedTripGroups[selectedTrip].map((order, idx) => {
                            const qty = order.items?.length > 0 ? order.items.reduce((sum, i) => sum + (i.qty || 0), 0) : (order.total_qty || order.quantity_kg || 0);
                            const hasRemark = !!(order.remark || order.notes);
                            return (
                              <div key={order.id} className="grid grid-cols-[14px_25px_60px_0.8fr_70px_1fr_40px_24px] gap-1 px-2 py-1.5 border-b text-xs items-center bg-green-50/30">
                                <span>{hasRemark && <span className="inline-block w-2.5 h-2.5 rounded-full bg-orange-400 cursor-pointer" title={order.remark || order.notes} onClick={(e) => { e.stopPropagation(); toast.info(order.remark || order.notes, { duration: 5000 }); }}></span>}</span>
                                <span className="font-bold text-green-700">{order.loading_seq || idx + 1}</span>
                                <span className="font-mono font-semibold text-[10px]">{order.order_number}</span>
                                <span className="truncate text-[10px] text-muted-foreground">{order.dispatch_name || order.customer_name}</span>
                                <span className="truncate text-[10px] text-gray-600">{getTransportName(order) || '-'}</span>
                                <span className="text-[10px] truncate">{getGradeDisplay(order)}</span>
                                <span className="text-right font-bold text-[10px]">{qty}</span>
                                <div className="text-center">
                                  <Button size="sm" variant="outline" className="h-5 w-5 p-0" onClick={() => printToken(order, 'trip_summary')} title="Print Token">
                                    <Printer className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => printTripSummary(selectedTrip)} className="h-7 text-xs">
                        <Printer className="w-3 h-3 mr-1" /> Print Summary
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openTripDetailsModal(selectedTrip)} className="h-7 text-xs">
                        <Edit className="w-3 h-3 mr-1" /> Edit Details
                      </Button>
                      
                      {/* Show Dispatch button if there are ready orders for this trip */}
                      {currentTripOrders.length > 0 && (
                        <Button 
                          size="sm" 
                          className="h-7 text-xs bg-green-600 hover:bg-green-700"
                          onClick={() => dispatchTrip(selectedTrip)}
                        >
                          <Truck className="w-3 h-3 mr-1" /> Dispatch {selectedTrip}
                        </Button>
                      )}
                      
                      {/* Show Reverse button if there are dispatched orders for this trip today */}
                      {dispatchedTripGroups[selectedTrip]?.length > 0 && (
                        <Button 
                          size="sm" 
                          variant="destructive"
                          className="h-7 text-xs"
                          onClick={() => reverseDispatch(selectedTrip)}
                        >
                          <Undo2 className="w-3 h-3 mr-1" /> Reverse {selectedTrip} ({dispatchedTripGroups[selectedTrip]?.length})
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
                    <Truck className="w-12 h-12 mb-3 opacity-30" />
                    <p className="text-sm font-medium">{selectedTrip} - No Orders</p>
                    <p className="text-xs mt-1">Select a trip with orders to view details</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      ) : activeTab === 'returns' ? (
        /* Returns View - 2 Panel Layout */
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
          {/* Left: Returns List */}
          <Card className="lg:col-span-3">
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <RotateCcw className="w-4 h-4" />
                Pending ({returnsData.filter(r => r.status === 'pending').length}) | Confirmed ({returnsData.filter(r => r.status === 'confirmed').length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {returnsLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : returnsData.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">No returns</div>
              ) : (
                <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
                  <div className="grid grid-cols-[70px_70px_65px_1fr_45px_55px_30px] gap-1 px-2 py-1.5 bg-secondary/50 text-[10px] font-semibold border-b sticky top-0">
                    <span>RETURN #</span><span>DATE</span><span>ORDER</span><span>CUSTOMER</span><span className="text-right">QTY</span><span className="text-center">STATUS</span><span></span>
                  </div>
                  {returnsData.map(r => (
                    <div key={r.id} onClick={() => setSelectedReturn(r)} className={`px-2 py-2 border-b cursor-pointer ${selectedReturn?.id === r.id ? 'bg-primary/10 border-l-2 border-l-primary' : 'hover:bg-secondary/30'} ${r.status === 'confirmed' ? 'bg-green-50/30' : ''}`}>
                      <div className="grid grid-cols-[70px_70px_65px_1fr_45px_55px_30px] gap-1 items-center text-xs">
                        <span className="font-mono font-semibold text-[10px] text-blue-700">{r.return_number}</span>
                        <span className="text-[10px]">{r.return_date?.slice(5) || '-'}</span>
                        <span className="font-mono text-[10px]">{r.order_number}</span>
                        <span className="truncate text-[10px]">{r.dispatch_name || r.customer_name}</span>
                        <span className="text-right font-bold text-[10px]">{r.return_qty}</span>
                        <span className="text-center">
                          {r.status === 'confirmed' ? (
                            <Badge className="bg-green-100 text-green-800 border-green-300 text-[8px] px-1">Done</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[8px] px-1">New</Badge>
                          )}
                        </span>
                        <span className="text-center">
                          <Button size="sm" variant="ghost" className={`h-5 w-5 p-0 ${r.posted_to_sheet ? 'text-green-600' : 'text-red-500'}`} onClick={(e) => { e.stopPropagation(); postReturnToSheet(r.id); }} disabled={postingReturnId === r.id} title={r.posted_to_sheet ? 'Posted' : 'Click to post'}>
                            {postingReturnId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3" />}
                          </Button>
                        </span>
                      </div>
                      {/* Return details line: grades, remark, confirmed by */}
                      <div className="mt-1 pl-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[9px] text-muted-foreground">
                        <span className="font-medium text-foreground/70">{(r.items || []).map(it => `${it.grade}/${it.sku} x${it.qty}`).join(', ')}</span>
                        {r.remark && <span className="italic text-amber-700 truncate max-w-[180px]" title={r.remark}>{r.remark}</span>}
                        {r.condition && <span className="text-slate-500">({r.condition})</span>}
                        {r.status === 'confirmed' && r.confirmed_by_username && (
                          <span className="text-green-700 font-medium">By: {r.confirmed_by_username}</span>
                        )}
                        {r.dispatch_number && <span className="font-mono text-slate-500">{r.dispatch_number}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right: Return Summary & Confirm */}
          <Card className="lg:col-span-2">
            <CardContent className="p-3">
              {selectedReturn ? (
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-lg">{selectedReturn.return_number}</h3>
                      <p className="text-xs text-muted-foreground">{selectedReturn.dispatch_name || selectedReturn.customer_name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedReturn.status === 'confirmed' ? (
                        <Badge className="bg-green-100 text-green-800 border-green-300">Confirmed</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-300">Pending</Badge>
                      )}
                      <Button size="sm" variant="ghost" className={`h-7 w-7 p-0 ${selectedReturn.posted_to_sheet ? 'text-green-600' : 'text-red-500'}`} onClick={() => postReturnToSheet(selectedReturn.id)} disabled={postingReturnId === selectedReturn.id} title={selectedReturn.posted_to_sheet ? 'Posted to Sheets' : 'Post to Google Sheets'}>
                        {postingReturnId === selectedReturn.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Order Info */}
                  <div className="grid grid-cols-2 gap-2 text-xs p-2 bg-secondary/30 rounded">
                    <div><span className="text-muted-foreground">Order:</span> <span className="font-mono font-bold">{selectedReturn.order_number}</span></div>
                    <div><span className="text-muted-foreground">Dispatch:</span> <span className="font-mono">{selectedReturn.dispatch_number || '-'}</span></div>
                    <div><span className="text-muted-foreground">Date:</span> <span>{selectedReturn.return_date}</span></div>
                    <div><span className="text-muted-foreground">Received:</span> <span>{selectedReturn.received_by || '-'}</span></div>
                    <div><span className="text-muted-foreground">Vehicle:</span> <span>{selectedReturn.vehicle_number || '-'}</span></div>
                    <div><span className="text-muted-foreground">Brought By:</span> <span>{selectedReturn.brought_by || '-'}</span></div>
                  </div>

                  {/* Return Items - Grade Summary */}
                  <div>
                    <h4 className="text-xs font-semibold mb-1.5">Return Grades</h4>
                    <div className="border rounded overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-secondary/40">
                            <TableHead className="text-[10px]">Grade</TableHead>
                            <TableHead className="text-[10px]">SKU</TableHead>
                            <TableHead className="text-[10px] text-right">Qty</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(selectedReturn.items || []).map((item, i) => (
                            <TableRow key={i}>
                              <TableCell className="text-xs font-medium">{item.grade}</TableCell>
                              <TableCell className="text-xs">{item.sku}</TableCell>
                              <TableCell className="text-xs text-right font-bold">{item.qty}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-secondary/20 font-bold">
                            <TableCell className="text-xs" colSpan={2}>Total Return</TableCell>
                            <TableCell className="text-xs text-right font-bold">{selectedReturn.return_qty}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  {/* Remark */}
                  {selectedReturn.remark && (
                    <div className="text-xs">
                      <span className="text-muted-foreground">Remark:</span>
                      <p className="mt-0.5 bg-amber-50 border border-amber-200 rounded p-2 text-amber-900">{selectedReturn.remark}</p>
                    </div>
                  )}

                  {selectedReturn.condition && (
                    <div className="text-xs">
                      <span className="text-muted-foreground">Condition:</span> <span className="font-medium">{selectedReturn.condition}</span>
                    </div>
                  )}

                  {/* Confirmed By */}
                  {selectedReturn.status === 'confirmed' && selectedReturn.confirmed_by_username && (
                    <div className="text-xs p-2 bg-green-50 border border-green-200 rounded">
                      <span className="text-green-800 font-medium">Confirmed by {selectedReturn.confirmed_by_username}</span>
                      {selectedReturn.confirmed_at && <span className="text-green-600 ml-2">({new Date(selectedReturn.confirmed_at).toLocaleString('en-IN', {day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',hour12:true})})</span>}
                    </div>
                  )}

                  {/* Confirm Button */}
                  {selectedReturn.status === 'pending' && (
                    <Button onClick={() => confirmReturn(selectedReturn.id)} disabled={confirmingReturnId === selectedReturn.id} className="w-full h-9 gap-2 bg-green-600 hover:bg-green-700" data-testid="confirm-return-btn">
                      {confirmingReturnId === selectedReturn.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      Confirm Return & Credit to Ledger
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground text-sm">
                  <RotateCcw className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  Select a return to view details
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        /* To Sort / Ready to Dispatch Layout */
        <div className="space-y-3">
          {/* Search bar for To Sort / Ready sections */}
          <div className="flex justify-end">
            <div className="relative">
              <Search className="w-3 h-3 absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <Input 
                type="text"
                placeholder="Search order #..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 w-40 pl-7 text-xs"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
          <Card className="lg:col-span-3">
            <CardContent className="p-0">
              <div className="p-2 border-b bg-secondary/30">
                <p className="text-sm font-medium">{activeTab === 'to_sort' ? 'Orders to Sort' : 'Ready to Dispatch'} ({displayOrders.length})</p>
              </div>
              {activeTab === 'to_sort' ? (
                <div className="grid grid-cols-[14px_90px_70px_110px_60px_80px_70px_70px_40px] gap-1 px-2 py-1.5 bg-secondary/50 text-[10px] font-semibold border-b">
                  <span></span><span>SORTING BY</span><span>ORDER</span><span>DISPATCH CODE</span><span className="text-right">QTY</span><span className="text-center">STATUS</span><span className="text-center">ACTION</span><span className="text-center">TRIP</span><span className="text-center">REV</span>
                </div>
              ) : (
                <div className="grid grid-cols-[14px_90px_70px_90px_70px_60px_70px_60px_50px_40px] gap-2 px-2 py-1.5 bg-secondary/50 text-[10px] font-semibold border-b">
                  <span></span><span>SORTED BY</span><span>ORDER</span><span>DISPATCH CODE</span><span>TRANSPORT</span><span className="text-right">QTY</span><span className="text-center">TRIP</span><span className="text-center">SEQ</span><span className="text-center">ACTION</span><span className="text-center">REV</span>
                </div>
              )}
              {loading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
              ) : displayOrders.length > 0 ? (
                <div className="max-h-[calc(100vh-280px)] overflow-y-auto">
                  {displayOrders.map((order) => {
                    const qty = order.items?.length > 0 
                      ? order.items.reduce((sum, i) => sum + (i.qty || 0), 0) 
                      : (order.total_qty || order.quantity_kg || 0);
                    const dispatchCode = order.dispatch_code || '';
                    const transportName = getTransportName(order);
                    
                    return activeTab === 'to_sort' ? (
                      <div key={order.id} onClick={() => setSelectedOrder(order)} className={`grid grid-cols-[14px_90px_70px_110px_60px_80px_70px_70px_40px] gap-1 px-2 py-1.5 border-b cursor-pointer text-xs items-center ${selectedOrder?.id === order.id ? 'bg-primary/10 border-l-2 border-l-primary' : 'hover:bg-secondary/30'} ${isSearchMatch(order) ? 'bg-yellow-100 border-l-4 border-l-yellow-500' : ''} ${order.warehouse_status === 'preparing' ? 'bg-amber-50' : ''}`}>
                        <span>{(order.remark || order.notes) && <span className="inline-block w-2.5 h-2.5 rounded-full bg-orange-400 cursor-pointer" title={order.remark || order.notes} onClick={(e) => { e.stopPropagation(); toast.info(order.remark || order.notes, { duration: 5000 }); }}></span>}</span>
                        <div className="truncate text-[10px]">
                          <span className="text-blue-600 font-medium">{order.sent_to_sort_by_username || '-'}</span>
                          {order.sent_to_sort_at && <div className="text-[8px] text-muted-foreground">{new Date(order.sent_to_sort_at).toLocaleString('en-IN', {day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',hour12:true})}</div>}
                        </div>
                        <span className={`font-mono font-semibold text-[10px] ${isSearchMatch(order) ? 'text-yellow-800 bg-yellow-200 px-1 rounded' : ''}`}>{order.order_number}</span>
                        <span className="truncate font-medium text-green-700 text-[10px]">{dispatchCode ? `${dispatchCode} (${qty})` : '-'}</span>
                        <span className="text-right font-bold text-[10px]">{qty}</span>
                        <div className="text-center" onClick={(e) => e.stopPropagation()}>
                          <Select value={order.warehouse_status || 'none'} onValueChange={async (v) => {
                            const newStatus = v === 'none' ? null : v;
                            try {
                              await axios.put(`${API}/orders/${order.id}`, { warehouse_status: newStatus });
                              setOrders(prev => prev.map(o => o.id === order.id ? { ...o, warehouse_status: newStatus, preparing_at: newStatus === 'preparing' ? new Date().toISOString() : o.preparing_at } : o));
                            } catch(err) { toast.error("Failed to update status"); }
                          }}>
                            <SelectTrigger className={`h-6 w-[75px] text-[10px] px-1 ${order.warehouse_status === 'preparing' ? 'border-amber-500 bg-amber-100 text-amber-800 font-semibold' : ''}`}><SelectValue placeholder="-" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none" className="text-xs">-</SelectItem>
                              <SelectItem value="preparing" className="text-xs">Preparing</SelectItem>
                            </SelectContent>
                          </Select>
                          {order.preparing_at && order.warehouse_status === 'preparing' && <div className="text-[7px] text-amber-600 mt-0.5">{new Date(order.preparing_at).toLocaleString('en-IN', {day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',hour12:true})}</div>}
                        </div>
                        <div className="flex gap-1 justify-center" onClick={(e) => e.stopPropagation()}>
                          <Button size="sm" variant="outline" className="h-5 w-5 p-0" onClick={() => markAsSorted(order.id)} title="Mark Sorted">
                            <CheckCircle className="w-3 h-3 text-green-600" />
                          </Button>
                          <Button size="sm" variant="outline" className="h-5 w-5 p-0" onClick={() => printToken(order, 'to_sort')} title="Print Token">
                            <Printer className="w-3 h-3 text-blue-500" />
                          </Button>
                        </div>
                        <div className="text-center" onClick={(e) => e.stopPropagation()}>
                          <Select value={order.trip_no || 'none'} onValueChange={(v) => updateTripNo(order.id, v === 'none' ? null : v)}>
                            <SelectTrigger className="h-6 w-16 text-[10px] px-1"><SelectValue placeholder="-" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none" className="text-xs">-</SelectItem>
                              {tripOptions.map(t => (
                                <SelectItem 
                                  key={t} 
                                  value={t} 
                                  className="text-xs"
                                >
                                  {t} {dispatchedTripsToday.includes(t) && '(next day)'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
                          <Button size="sm" variant="outline" className="h-5 w-5 p-0" onClick={() => sendBackOrder(order.id, 'pending')} title="Send Back to Orders">
                            <Undo2 className="w-3 h-3 text-orange-500" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div key={order.id} onClick={() => setSelectedOrder(order)} className={`grid grid-cols-[14px_90px_70px_90px_70px_60px_70px_60px_50px_40px] gap-2 px-2 py-1.5 border-b cursor-pointer text-xs items-center ${selectedOrder?.id === order.id ? 'bg-primary/10 border-l-2 border-l-primary' : 'hover:bg-secondary/30'} ${isSearchMatch(order) ? 'bg-yellow-100 border-l-4 border-l-yellow-500' : ''}`}>
                        <span>{(order.remark || order.notes) && <span className="inline-block w-2.5 h-2.5 rounded-full bg-orange-400 cursor-pointer" title={order.remark || order.notes} onClick={(e) => { e.stopPropagation(); toast.info(order.remark || order.notes, { duration: 5000 }); }}></span>}</span>
                        <span className="truncate text-[10px] text-blue-600 font-medium">{order.sorted_by_username || '-'}</span>
                        <span className={`font-mono font-semibold text-[10px] ${isSearchMatch(order) ? 'text-yellow-800 bg-yellow-200 px-1 rounded' : ''}`}>{order.order_number}</span>
                        <span className="truncate font-medium text-green-700 text-[10px]">{dispatchCode ? `${dispatchCode} (${qty})` : '-'}</span>
                        <span className="truncate text-[10px] text-gray-600" onClick={(e) => { e.stopPropagation(); setTransportEdits(prev => ({ ...prev, [order.id]: transportName || '' })); }} title="Click to edit transport">
                          {transportEdits[order.id] !== undefined ? (
                            <Input 
                              value={transportEdits[order.id]} 
                              onChange={(e) => setTransportEdits(prev => ({ ...prev, [order.id]: e.target.value }))} 
                              onBlur={async () => {
                                try {
                                  await axios.put(`${API}/orders/${order.id}`, { transport_name: transportEdits[order.id] });
                                  fetchOrders();
                                } catch (err) { /* silent */ }
                                setTransportEdits(prev => { const n = {...prev}; delete n[order.id]; return n; });
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                              className="h-5 text-[10px] px-1 w-full" 
                              autoFocus 
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (transportName || '-')}
                        </span>
                        <span className="text-right font-bold text-[10px]">{qty}</span>
                        <div className="text-center" onClick={(e) => e.stopPropagation()}>
                          <Select value={order.trip_no || 'none'} onValueChange={(v) => updateTripNo(order.id, v === 'none' ? null : v)}>
                            <SelectTrigger className="h-6 w-16 text-[10px] px-1"><SelectValue placeholder="-" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none" className="text-xs">-</SelectItem>
                              {tripOptions.map(t => (
                                <SelectItem 
                                  key={t} 
                                  value={t} 
                                  className="text-xs"
                                >
                                  {t} {dispatchedTripsToday.includes(t) && '(next day)'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="text-center" onClick={(e) => e.stopPropagation()}>
                          <Input type="number" min="1" value={order.loading_seq || ''} onChange={(e) => updateLoadingSeq(order.id, e.target.value)} className="h-6 w-14 text-[10px] text-center px-1" placeholder="-" />
                        </div>
                        <div className="flex gap-1 justify-center" onClick={(e) => e.stopPropagation()}>
                          <Button size="sm" variant="outline" className="h-5 w-5 p-0" onClick={() => printToken(order, 'ready_to_dispatch')} title="Print Token">
                            <Printer className="w-3 h-3 text-blue-500" />
                          </Button>
                        </div>
                        <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
                          <Button size="sm" variant="outline" className="h-5 w-5 p-0" onClick={() => sendBackOrder(order.id, 'to_sort')} title="Send Back to Sort">
                            <Undo2 className="w-3 h-3 text-orange-500" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  {activeTab === 'to_sort' ? <Package className="w-10 h-10 mb-2 opacity-30" /> : <Truck className="w-10 h-10 mb-2 opacity-30" />}
                  <p className="text-sm">No orders</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardContent className="p-3">
              {selectedOrder ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold">{selectedOrder.order_number}</h3>
                      <p className="text-xs text-muted-foreground">{selectedOrder.dispatch_name || selectedOrder.customer_name}</p>
                    </div>
                    <Badge className={activeTab === 'to_sort' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}>{activeTab === 'to_sort' ? 'To Sort' : 'Ready'}</Badge>
                  </div>
                  {/* Order Remark */}
                  {(selectedOrder.remark || selectedOrder.notes) && (
                    <div className="text-xs bg-orange-50 border-l-4 border-orange-400 rounded p-2" data-testid="warehouse-remark-display">
                      <span className="font-bold text-orange-700">REMARK: </span>
                      <span className="text-orange-900">{selectedOrder.remark || selectedOrder.notes}</span>
                    </div>
                  )}
                  {activeTab === 'ready_to_dispatch' && (
                    <div className="grid grid-cols-2 gap-2 p-2 bg-blue-50 rounded border border-blue-200">
                      <div>
                        <label className="text-[10px] font-semibold text-blue-800">TRIP</label>
                        <Select value={selectedOrder.trip_no || 'none'} onValueChange={(v) => updateTripNo(selectedOrder.id, v === 'none' ? null : v)}>
                          <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="-" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-</SelectItem>
                            {tripOptions.map(t => (
                              <SelectItem 
                                key={t} 
                                value={t}
                                disabled={dispatchedTripsToday.includes(t)}
                              >
                                {t} {dispatchedTripsToday.includes(t) && '(dispatched)'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-blue-800">SEQ</label>
                        <Input type="number" min="1" value={selectedOrder.loading_seq || ''} onChange={(e) => updateLoadingSeq(selectedOrder.id, e.target.value)} className="h-7 text-xs text-center" placeholder="-" />
                      </div>
                    </div>
                  )}
                  <div className="border rounded overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-blue-100">
                          <TableHead className="text-[10px] py-1 px-2">#</TableHead>
                          <TableHead className="text-[10px] py-1 px-2">Grade</TableHead>
                          <TableHead className="text-[10px] py-1 px-2">Pack</TableHead>
                          <TableHead className="text-[10px] py-1 px-2 text-right">Qty</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedOrder.items?.length > 0 ? selectedOrder.items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="text-xs py-1 px-2">{idx + 1}</TableCell>
                            <TableCell className="text-xs py-1 px-2 font-semibold">{item.grade}</TableCell>
                            <TableCell className="text-xs py-1 px-2">{item.sku}</TableCell>
                            <TableCell className="text-xs py-1 px-2 text-right font-bold">{item.qty}</TableCell>
                          </TableRow>
                        )) : (
                          <TableRow>
                            <TableCell className="text-xs py-1 px-2">1</TableCell>
                            <TableCell className="text-xs py-1 px-2 font-semibold">{selectedOrder.product_grade}</TableCell>
                            <TableCell className="text-xs py-1 px-2">{selectedOrder.packing}</TableCell>
                            <TableCell className="text-xs py-1 px-2 text-right font-bold">{selectedOrder.quantity_kg}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  {activeTab === 'to_sort' && (
                    <Button className="w-full h-7 text-xs bg-yellow-600 hover:bg-yellow-700" onClick={() => markAsSorted(selectedOrder.id)}>
                      <CheckCircle className="w-3 h-3 mr-1" /> Mark as Sorted
                    </Button>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
                  <Package className="w-10 h-10 mb-2 opacity-30" />
                  <p className="text-sm">Select an order</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        </div>
      )}

      {/* Trip Details Modal */}
      <Dialog open={showTripDetailsModal} onOpenChange={setShowTripDetailsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" /> {editingTrip} Details
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium">Driver Name</label><Input value={tripForm.driver_name} onChange={(e) => setTripForm({...tripForm, driver_name: e.target.value})} placeholder="Driver name" /></div>
              <div><label className="text-xs font-medium">Driver Phone</label><Input value={tripForm.driver_phone} onChange={(e) => setTripForm({...tripForm, driver_phone: e.target.value})} placeholder="Phone" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium">Vehicle Number</label><Input value={tripForm.vehicle_number} onChange={(e) => setTripForm({...tripForm, vehicle_number: e.target.value})} placeholder="Vehicle no." /></div>
              <div><label className="text-xs font-medium">Dispatch Time</label><Input value={tripForm.dispatch_time} onChange={(e) => setTripForm({...tripForm, dispatch_time: e.target.value})} placeholder="10:30 AM" /></div>
            </div>
            <div><label className="text-xs font-medium">Notes</label><Textarea value={tripForm.notes} onChange={(e) => setTripForm({...tripForm, notes: e.target.value})} placeholder="Notes..." rows={2} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowTripDetailsModal(false)}>Cancel</Button>
            <Button onClick={saveTripDetails}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Printer Settings Modal */}
      <Dialog open={showPrinterSettings} onOpenChange={setShowPrinterSettings}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5" /> Printer Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Configure your printer for this device. Each user can set their own printer based on their location.
            </p>
            <div>
              <label className="text-xs font-medium">Printer Name / ID</label>
              <Input 
                value={printerName} 
                onChange={(e) => setPrinterName(e.target.value)} 
                placeholder="e.g., Office Printer, Warehouse-1, HP LaserJet"
                className="mt-1"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                This is saved locally on your browser. Different users on different computers can have different printers.
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-xs">
              <p className="font-semibold text-blue-800 mb-1">How it works:</p>
              <ul className="text-blue-700 space-y-1 list-disc list-inside">
                <li>When you click Print, your browser's print dialog opens</li>
                <li>Select your connected printer from the dialog</li>
                <li>The printer name here is for your reference only</li>
              </ul>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowPrinterSettings(false)}>Cancel</Button>
            <Button onClick={savePrinterSettings}>Save Settings</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Tracking Dialog */}
      <Dialog open={showTracking} onOpenChange={setShowTracking}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto p-4">
          <DialogHeader className="pb-2 border-b">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Activity className="w-5 h-5" /> Order Tracking
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              {['day', 'week', 'month'].map(v => (
                <Button key={v} size="sm" variant={trackingView === v ? 'default' : 'outline'} onClick={() => setTrackingView(v)} className="h-7 text-xs capitalize">
                  {v === 'day' ? 'Day' : v === 'week' ? 'Week' : 'Month'}
                </Button>
              ))}
              <div className="flex items-center gap-1 ml-2">
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => {
                  const d = new Date(trackingDate);
                  if (trackingView === 'day') d.setDate(d.getDate() - 1);
                  else if (trackingView === 'week') d.setDate(d.getDate() - 7);
                  else d.setMonth(d.getMonth() - 1);
                  setTrackingDate(d);
                }}>&lt;</Button>
                <span className="text-sm font-medium min-w-[140px] text-center">
                  {trackingView === 'day' && format(trackingDate, 'dd MMM yyyy')}
                  {trackingView === 'week' && `${format(startOfWeek(trackingDate, {weekStartsOn:1}), 'dd MMM')} - ${format(endOfWeek(trackingDate, {weekStartsOn:1}), 'dd MMM yyyy')}`}
                  {trackingView === 'month' && format(trackingDate, 'MMMM yyyy')}
                </span>
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => {
                  const d = new Date(trackingDate);
                  if (trackingView === 'day') d.setDate(d.getDate() + 1);
                  else if (trackingView === 'week') d.setDate(d.getDate() + 7);
                  else d.setMonth(d.getMonth() + 1);
                  setTrackingDate(d);
                }}>&gt;</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs ml-1" onClick={() => setTrackingDate(new Date())}>Today</Button>
              </div>
              <Badge variant="outline" className="ml-auto text-xs">{getTrackingFiltered().length} orders</Badge>
            </div>
            {trackingLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
              <div className="border rounded overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/50">
                      <TableHead className="text-xs w-[70px]">Order #</TableHead>
                      <TableHead className="text-xs">Customer</TableHead>
                      <TableHead className="text-xs text-right w-[45px]">Qty</TableHead>
                      <TableHead className="text-xs w-[50px]">Status</TableHead>
                      <TableHead className="text-xs text-center"><div className="flex flex-col items-center"><Package className="w-3 h-3 mb-0.5" /><span>To Sort</span></div></TableHead>
                      <TableHead className="text-xs text-center"><div className="flex flex-col items-center"><Clock className="w-3 h-3 mb-0.5" /><span>Preparing</span></div></TableHead>
                      <TableHead className="text-xs text-center"><div className="flex flex-col items-center"><Check className="w-3 h-3 mb-0.5" /><span>Ready</span></div></TableHead>
                      <TableHead className="text-xs text-center"><div className="flex flex-col items-center"><ClipboardList className="w-3 h-3 mb-0.5" /><span>Sequence</span></div></TableHead>
                      <TableHead className="text-xs text-center"><div className="flex flex-col items-center"><Truck className="w-3 h-3 mb-0.5" /><span>Dispatched</span></div></TableHead>
                      <TableHead className="text-xs text-center w-[60px]">Total Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getTrackingFiltered().length === 0 ? (
                      <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground text-sm">No orders found for this period</TableCell></TableRow>
                    ) : getTrackingFiltered().map(o => (
                      <TableRow key={o.id} className={o.status === 'dispatched' ? 'bg-green-50/50' : o.warehouse_status === 'preparing' ? 'bg-amber-50/50' : ''}>
                        <TableCell className="text-xs font-mono font-semibold">{o.order_number}</TableCell>
                        <TableCell className="text-xs truncate max-w-[120px]">{o.customer_name}</TableCell>
                        <TableCell className="text-xs text-right font-bold">{o.qty}</TableCell>
                        <TableCell className="text-xs">
                          <Badge variant="outline" className={`text-[9px] ${
                            o.status === 'dispatched' ? 'bg-green-100 text-green-800 border-green-300' :
                            o.status === 'ready_to_dispatch' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                            o.warehouse_status === 'preparing' ? 'bg-amber-100 text-amber-800 border-amber-300' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {o.status === 'dispatched' ? 'Done' : o.status === 'ready_to_dispatch' ? 'Ready' : o.warehouse_status === 'preparing' ? 'Prep' : 'Sort'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[10px] text-center">{fmtTs(o.sent_to_sort_at)}</TableCell>
                        <TableCell className="text-[10px] text-center">{fmtTs(o.preparing_at)}</TableCell>
                        <TableCell className="text-[10px] text-center">{fmtTs(o.ready_at)}</TableCell>
                        <TableCell className="text-[10px] text-center">{fmtTs(o.sequenced_at)}</TableCell>
                        <TableCell className="text-[10px] text-center">{fmtTs(o.dispatched_at)}</TableCell>
                        <TableCell className="text-[10px] text-center font-medium">
                          {fmtDuration(o.sent_to_sort_at, o.dispatched_at || o.ready_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
